import { sellOrderStatusInversed } from '@teinor/erp/types/company/order/sell/sellOrder'
import { SellCreateWorkSheetButton, SellDownloadButtonCSV, SellDownloadButtonPDF, SellDownloadPdfTemplateButton, SellDownloadTags, SellPdfPreview, SellViewWorkSheetButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout/SellButtons'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { SellOrderDuplicateButton } from '../../../SellOrderButtons'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { useMemo } from 'react'
import { getAllWorkSheetGroupStatuses } from '../../../../../../../../../../../utils/reducers/getters'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import { OrderDivideIntoChaptersButton, OrderProductTypesButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'

const SellOrderCreateNoPermissionButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useSellOrderCreateNoPermissionButtonsLogic, ...rest }} className='mr-8' />
    )
}

const useSellOrderCreateNoPermissionButtonsLogic = ({ data, onSubmit, isReadOnly }: OrderStepButtonsBaseComponentProps) => {
    const {t} = useTwinTranslation()
    const name = 'sell_order_create_no_permission_step_buttons'
    const workSheets = getAllWorkSheetGroupStatuses()
    const Order = data?.header
    const showButtonByPricesPermission = !Order?.hidePrices
    const showButtonByStep = Order?.status !== sellOrderStatusInversed['creating']
    const showCreateWorkSheetButton = showButtonByStep && !Order?.WorkSheetId && !!workSheets?.WorkSheetActiveStatusGroups?.length

    const defaultButtons = [{ "id": "previewSellOrder" }, { "id": "downloadSellOrder" }, { "id": "duplicateSell" }, { "id": "createWorkSheet" }, { "id": "viewWorkSheet" }]

    const sellOrderDuplicateOrderText = t('duplicateOrder', 'Duplicar factura')
    const sellOrderDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const sellOrderPrintTagsText = t('printTags', 'Imprimir etiquetas')
    const sellOrderPreviewPDFText = t('previewPdf', 'Previsualizar PDF')
    const sellOrderDownloadPDFTemplateText = t('downloadSelectingPdfTemplate', 'Descargar seleccionando plantilla PDF')
    const sellOrderDownloadPDFText = t('downloadPdf', 'Descargar PDF')
    const sellViewWorkSheetText = t('goToWorkSheet', 'Ir a la hoja de trabajo')
    const sellCreateWorkSheetText = t('createWorkSheet', 'Crear hoja de trabajo')
    const sellOrderDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const sellOrderProductTypesText = t('valueProductTypes', 'Valor tipos de producto')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
            return {
                duplicateSell: {
                    component: SellOrderDuplicateButton,
                    showButton: showButtonByPricesPermission,
                    extraProps: {
                        title: sellOrderDuplicateOrderText,
                        id: 'duplicateSell',
                        allRowData: data,
                        key:'duplicateButton'
                    }
                },
                downloadSellCsv: {
                    component: SellDownloadButtonCSV,
                    showButton: showButtonByPricesPermission,
                    extraProps: {
                        title: sellOrderDownloadCSVText,
                        id: 'downloadSellCsv',
                        Order,
                        typeTemplate: 'sellOrder',
                    }
                },
                downloadSellTag: {
                    component: SellDownloadTags,
                    showButton: showButtonByPricesPermission,
                    extraProps: {
                        title: sellOrderPrintTagsText,
                        id: 'downloadSellTag',
                        Order,
                        typeTemplate: 'sellOrder',
                    }
                },
                previewSellOrder: {
                    component: SellPdfPreview,
                    showButton: showButtonByPricesPermission,
                    extraProps: {
                        title: sellOrderPreviewPDFText,
                        id: 'previewSellOrder',
                        Order,
                        typeTemplate: 'sellOrder',
                    }
                },
                downloadSellOrderTemplate: {
                    component: SellDownloadPdfTemplateButton,
                    showButton: showButtonByPricesPermission,
                    extraProps: {
                        title: sellOrderDownloadPDFTemplateText,
                        id: 'downloadSellOrderTemplate',
                        Order,
                        typeTemplate: 'sellOrder',
                        key: 'downloadSellOrderPDFTemplate'
                    }
                },
                downloadSellOrder: {
                    component: SellDownloadButtonPDF,
                    showButton: showButtonByPricesPermission,
                    extraProps: {
                        title: sellOrderDownloadPDFText,
                        id: 'downloadSellOrder',
                        Order,
                        typeTemplate: 'sellOrder',
                        key: 'downloadSellOrder'
                    }
                },
                divideIntoChaptersQuote: {
                    component: OrderDivideIntoChaptersButton,
                    extraProps: {
                        title: sellOrderDivideIntoChaptersText,
                        id: 'divideIntoChaptersQuote',
                        allRowData: data,
                        onSubmit,
                        isReadOnly,
                    }
                },
                viewWorkSheet: {
                    component: SellViewWorkSheetButton,
                    showButton: showButtonByStep && Order?.WorkSheetId,
                    extraProps: {
                        title: sellViewWorkSheetText,
                        id: 'viewWorkSheet',
                        WorkSheetId: Order?.WorkSheetId
                    }
                },
                createWorkSheet: {
                    component: SellCreateWorkSheetButton,
                    showButton: showCreateWorkSheetButton,
                    extraProps: {
                        title: sellCreateWorkSheetText,
                        id: 'createWorkSheet',
                        Order,
                        createURL: '/api/app/order/sell/workSheet/createWorkSheetBySellOrder',
                        orderField: 'SellOrderId',
                        completeData: data
                    }
                },
                sellProductTypes: {
                    component: OrderProductTypesButton,
                    extraProps: {
                        title: sellOrderProductTypesText,
                        id: 'sellProductTypes',
                        allRowData: data,
                        onSubmit,
                        isReadOnly
                    }
                },
            }
    }, [Order, data, onSubmit, sellCreateWorkSheetText, sellOrderDivideIntoChaptersText, sellOrderDownloadCSVText, sellOrderDownloadPDFTemplateText, sellOrderDownloadPDFText, sellOrderDuplicateOrderText, sellOrderPreviewPDFText, sellOrderPrintTagsText, sellViewWorkSheetText, isReadOnly, showButtonByPricesPermission, showButtonByStep, showCreateWorkSheetButton, sellOrderProductTypesText])

    return { name, ButtonComponents, defaultButtons }
}

export const SellOrderCreateNoPermissionButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useSellOrderCreateNoPermissionButtonsLogic, ...rest }} className='mr-8' />
    )
}

export default SellOrderCreateNoPermissionButtons